import axios from "axios";
import errorStatus from "../utils/static/errorStatus";
import { getAccessToken, setAccessToken, getAccessTokenExpiresAtMs } from "./accessTokenStore";
import {
  triggerRefreshSessionFailed,
  triggerTwoFaExpired,
  TWO_FA_EXPIRED_MESSAGE,
} from "./authSessionEvents";
import { syncSessionExpiryFromPayload } from "./refreshSessionMeta";

const baseURL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL,
  timeout: 100000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const refreshClient = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const NO_REFRESH_PATH_FRAGMENTS = [
  "/auth/login",
  "auth/login",
  "/auth/verify-login",
  "auth/verify-login",
  "/auth/refresh-session",
  "auth/refresh-session",
  "/auth/logout",
  "auth/logout",
  "/auth/register",
  "auth/register",
  "/auth/forgot-password",
  "auth/forgot-password",
  "/auth/reset-password",
  "auth/reset-password",
  "/auth/verify",
  "auth/verify",
  "/auth/sso/microsoft/start",
  "auth/sso/microsoft/login",
  "auth/sso/microsoft/register",
  "/sub-user/verifySubUser",
  "sub-user/verifySubUser",
  "/subsidiary/verify",
  "subsidiary/verify",
];

function shouldSkipAuthRefresh(config) {
  const url = `${config.baseURL || ""}${config.url || ""}`;
  return NO_REFRESH_PATH_FRAGMENTS.some((frag) => url.includes(frag));
}

const ACCESS_TOKEN_REFRESH_SKEW_MS = 90 * 1000;
const OAUTH_BOUNCE_RETRY_DELAY_MS = 400;
const IRAS_OAUTH_IN_FLIGHT_KEY = "iras_oauth_in_flight";

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isIrasOAuthInFlight() {
  try {
    const raw =
      sessionStorage.getItem(IRAS_OAUTH_IN_FLIGHT_KEY) ||
      localStorage.getItem(IRAS_OAUTH_IN_FLIGHT_KEY);
    if (!raw) return false;
    const ts = Number(JSON.parse(raw)?.ts);
    return Number.isFinite(ts) && Date.now() - ts < 30 * 60 * 1000;
  } catch {
    return false;
  }
}

let refreshPromise = null;

class MissingRefreshedAccessTokenError extends Error {
  constructor(message) {
    super(message);
    this.name = "MissingRefreshedAccessTokenError";
  }
}

function isSessionEndingRefreshError(error) {
  if (error instanceof MissingRefreshedAccessTokenError) {
    return true;
  }

  const status =
    error?.response?.status ??
    error?.statusCode ??
    error?.error?.response?.status;
  return status === 401 || status === 403;
}

function normalizeAccessToken(token) {
  return typeof token === "string" && token.trim() ? token : null;
}

async function postRefreshSession() {
  const headers = {};
  const current = normalizeAccessToken(getAccessToken());
  if (current) {
    headers.Authorization = `Bearer ${current}`;
  }

  const res = await refreshClient.post("/auth/refresh-session", {}, { headers });
  const body = res.data;
  const inner = body?.data ?? body;
  const access = normalizeAccessToken(inner?.accessToken);
  if (!access) {
    throw new MissingRefreshedAccessTokenError(
      "Refresh response did not include an access token"
    );
  }
  setAccessToken(access);
  const storedAccess = normalizeAccessToken(getAccessToken());
  if (!storedAccess) {
    throw new MissingRefreshedAccessTokenError(
      "Refreshed access token was not stored"
    );
  }
  // The server may include refreshToken for rotation-race recovery. The
  // HttpOnly cookie remains the authoritative refresh-token storage; do not
  // copy the raw token into localStorage or application state.
  syncSessionExpiryFromPayload(inner, { isRefresh: true });
  return storedAccess;
}

async function refreshAccessTokenOnce() {
  try {
    return await postRefreshSession();
  } catch (error) {
    // Only retry after a CorpPass bounce. Retrying every 401 can burn a
    // rotating refresh cookie that Safari already replaced on the first POST.
    if (!isSessionEndingRefreshError(error) || !isIrasOAuthInFlight()) {
      throw error;
    }
    await wait(OAUTH_BOUNCE_RETRY_DELAY_MS);
    return await postRefreshSession();
  }
}

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessTokenOnce().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Used during application bootstrap when the access token is missing but the
// HttpOnly refresh cookie may still be valid.
export async function restoreAccessToken() {
  return refreshAccessToken();
}

function isAccessTokenFresh(skewMs = ACCESS_TOKEN_REFRESH_SKEW_MS) {
  const token = normalizeAccessToken(getAccessToken());
  if (!token) return false;
  const exp = getAccessTokenExpiresAtMs(token);
  if (exp == null) return true;
  return exp - Date.now() > skewMs;
}

/**
 * Refresh the access token before it expires, while the tab is first-party.
 * Safari ITP often drops the HttpOnly refresh cookie if we only wake it on 401.
 */
export async function keepSessionAlive() {
  const token = normalizeAccessToken(getAccessToken());
  if (!token) return null;
  if (isAccessTokenFresh()) return token;

  try {
    return await refreshAccessToken();
  } catch {
    return isAccessTokenFresh(0) ? normalizeAccessToken(getAccessToken()) : null;
  }
}

function transformAxiosError(error) {
  const { response } = error;
  const statusCode = response?.status || 500;
  const errorMessageConfig = errorStatus[statusCode] || errorStatus.default;
  if (errorMessageConfig.action) errorMessageConfig.action();
  return Promise.reject({
    success: false,
    statusCode,
    message:
      response?.data?.message ||
      errorMessageConfig.message ||
      "An unexpected error occurred",
    error,
  });
}

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response.data || response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const bodyMsg = error.response?.data?.message;
    const msgStr = typeof bodyMsg === "string" ? bodyMsg : "";
    if (status === 401 && msgStr.includes(TWO_FA_EXPIRED_MESSAGE)) {
      triggerTwoFaExpired();
      return transformAxiosError(error);
    }

    if (!originalRequest || originalRequest.skipAuthRefresh) {
      return transformAxiosError(error);
    }

    if (status === 401 && !originalRequest._retry) {
      if (shouldSkipAuthRefresh(originalRequest)) {
        return transformAxiosError(error);
      }

      originalRequest._retry = true;
      try {
        const token = normalizeAccessToken(await refreshAccessToken());
        if (!token) {
          throw new MissingRefreshedAccessTokenError(
            "Refresh completed without an access token"
          );
        }
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api.request(originalRequest);
      } catch (refreshError) {
        if (isSessionEndingRefreshError(refreshError)) {
          triggerRefreshSessionFailed();
          return transformAxiosError(error);
        }

        return transformAxiosError(refreshError);
      }
    }

    return transformAxiosError(error);
  }
);

export default api;
